// Cached Intl formatters: construction is non-trivial and these run many times
// per render in report tables and EffectiveRateBreakdown.
const intFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const preciseFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// 2–3 decimal USD. Shows the 3rd decimal only when meaningful (22.555 → "$22.555");
// used for hourly, scale, and fringe rates where certified-payroll precision matters.
const rateFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
});

export interface FormatCurrencyOptions {
  /** Show 2 decimal places with thousand separators. Defaults to 0 decimals. */
  precise?: boolean;
  /** Returned for null/undefined. Defaults to 'N/A'. */
  nullText?: string;
}

/**
 * Formats a number as a USD currency string with thousand separators.
 * Defaults to 0 decimals (e.g. 1234 → "$1,234"); pass `precise` for cents
 * (e.g. 1234.5 → "$1,234.50"). Returns `nullText` ('N/A' by default) for
 * null/undefined.
 * @registry Canonical USD currency formatter; use instead of hand-rolled Intl/toFixed formatting.
 */
export const formatCurrency = (
  value?: number | null,
  options: FormatCurrencyOptions = {},
): string => {
  if (value === undefined || value === null) return options.nullText ?? 'N/A';
  return (options.precise ? preciseFormatter : intFormatter).format(value);
};

/**
 * Formats a number as a USD rate string with 2–3 decimal precision.
 * Shows the 3rd decimal only when meaningful (22.555 → "$22.555"), otherwise
 * standard 2-decimal currency (22 → "$22.00"). Returns "N/A" for null/undefined.
 * @registry Certified-payroll-precision rate formatter (hourly/scale/fringe rates); use instead of formatCurrency for pay rates.
 */
export const formatRate = (value?: number | null): string => {
  if (value === undefined || value === null) return 'N/A';
  return rateFormatter.format(value);
};
